/**
 * Shared display formatters for admin tables and detail panels.
 * Prefer these over raw ISO strings or enum keys in UI.
 */

/** "Jan 5, 2026, 6:00 PM" (locale-aware). */
export function formatDateTime(
  value: string | number | Date | null | undefined,
): string {
  if (value == null || value === '') return '—'
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** "Jan 5, 2026" */
export function formatDate(
  value: string | number | Date | null | undefined,
): string {
  if (value == null || value === '') return '—'
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/** Compact age for queue density: "2h", "3d", "5w". */
export function formatAgeHours(hours: number): string {
  if (!Number.isFinite(hours) || hours < 0) return '—'
  if (hours < 1) return '<1h'
  if (hours < 48) return `${Math.floor(hours)}h`
  const days = Math.floor(hours / 24)
  if (days < 14) return `${days}d`
  const weeks = Math.floor(days / 7)
  if (weeks < 8) return `${weeks}w`
  return `${Math.floor(days / 30)}mo`
}

/** Held-for label on an advisory review claim (#1125). Minutes, then hours. */
export function formatClaimAge(minutes: number | null): string {
  if (minutes == null || !Number.isFinite(minutes) || minutes < 0) return '—'
  return minutes < 60 ? `${Math.floor(minutes)}m` : formatAgeHours(minutes / 60)
}

/** Title-case a snake/kebab/lowercase enum for badges. */
export function formatLabel(value: string | null | undefined): string {
  if (value == null || value === '') return '—'
  return value
    .replaceAll(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

/** Resolve display label from option list, else formatLabel. */
export function labelFromOptions(
  value: string,
  options?: readonly { label: string; value: string }[],
): string {
  const hit = options?.find((o) => o.value === value)
  return hit?.label ?? formatLabel(value)
}
